import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  HomeContainer,
  MainArea,
  ChatContainer,
  BodyChatContainer,
  FooterChatContainer,
  ChatInputWrapper,
  Separator,
  CustomGenericListStyle,
  ChatConversationContainer,
  ChatWindowWrapper,
  WelcomeContainer,
  ImageContainer,
  AgentWelcomeText,
  AgentNameText,
  AgentWelcomeContainer,
  CenteredSpinner,
  AgentImage,
  ModelSelectorBar,
  ModelSelectLabel
} from "./HomePage.styled";
import { ChatInput } from "../../../elements/v2/Input";
import Spinner from "../../../components/common/Spinner";
import { useStore, useWebSocketScope } from "../../../hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toJS } from "mobx";
import SidebarAgents from "./components/SidebarAgents";
import EmptyAgentState from "../../HomeChatPage/components/EmptyAgentState";
import { observer } from "mobx-react";
import { ChatCommunicationService } from "../../../services/ChatCommunicationService";
import ChatQuestionsSection from "./components/ChatQuestionsSection";
import { BreadCrumbContainer, MainContainer, Title } from "../Conversations/Section/EventName/EventName.styled";
import dayjs from 'dayjs'
import styled from 'styled-components';
import {useMediaQueries}  from '../../../utils/mediaQueries';
import { Icon } from "../../../elements";
import { truncateText } from "../../../utils/uiUtils";
import HomePageChatWindow from "../../../components/DocumentCanvas/HomePageChatWindow";
import DocumentsDropdown from "../../../components/DocumentCanvas/DocumentsDropdown";
import DocumentCanvas from "../../../components/DocumentCanvas/DocumentCanvas";
import DocumentEditLayout from "../../../components/DocumentCanvas/DocumentEditLayout";
import ChatComponentPersistent from "../PMSystemPage/components/ChatComponent/ChatComponentPersistent";
import { ChatProvider } from "../PMSystemPage/context/ChatContext";
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import { detectChatTool } from '../../../utils/chatMessageDetector';
import DAGProgressIndicator from "../../../components/DocumentCanvas/components/DAGProgressIndicator";
import { Select, MenuItem, FormControl } from '@mui/material';
import { WebSocketScope } from '../../../utils/enums';
import { createDocument, createDocumentByTenant, attachDocumentbyTenant, assignAgentToDocument } from '../../../services/document';

const ResponsiveFooterChatContainer = styled(FooterChatContainer)`
  @media (max-width: 768px) {
    position: relative;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    padding: 0.5rem;
    background-color: ${props => props.theme.colors.white};
  }
    ${(props) => props.attachment && `
      &.footer-chat-container > div > div:nth-child(2) > div:first-child {
        background: ${props.theme.colors.primaryWhite};
        border-color: ${(props) => props.theme.colors.clearGray};
      }
    `}

    &.footer-chat-container > div > div:nth-child(2) > div:last-child {
        background: ${(props) => props.theme.colors.primaryWhite};
        border-color: ${(props) => props.theme.colors.clearGray};
        position: relative;
        bottom: 0;
    }
`;

const HomePage = observer(() => {
  const navigate = useNavigate();
  const { authStore, changeTitle, historyAgentStore, conversationsStore, playgroundConfigStore, chatDocumentsStore, fileStore } = useStore();
  const [searchParams] = useSearchParams();
  changeTitle(null);
  const [agentIsTyping, setAgentIsTyping] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [chatWindowHeight, setChatWindowHeight] = useState(48);
  const {isDesktop, isMobile,isTablet} = useMediaQueries();

  const [agent_params, setAgent_params] = useState({
    message: "",
    campaign_id: "",
    campaign_version: "",
    agent: "",
  });
  const [attachment, setAttachment] = useState({ url: "", fileName: "", fileSize: "" });
  const [persistentFileUuids, setPersistentFileUuids] = useState([]); // Store file_uuids to always send them
  const { conciergeAgent } = historyAgentStore;

  const [isSendMessageClicked, setIsSendMessageClicked] = useState(false);
  const [selectedAgentName, setSelectedAgentName] = useState("");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [chatDagProgress, setChatDagProgress] = useState([]);
  const [chatCurrentDagNode, setChatCurrentDagNode] = useState(null);
  const [isChatReasoningActive, setIsChatReasoningActive] = useState(false);
  const chatDagProgressRef = useRef([]); // Ref to track DAG progress synchronously
  const streamingMessagesRef = useRef(new Map()); // Ref to track streaming messages by messageId
  const streamingChunksRef = useRef(new Map()); // Ref to track chunks by messageId -> Map<chunkIndex, chunkContent>
  const expectedChunksRef = useRef(new Map()); // Ref to track expected total chunks by messageId (from COMPLETE event)
  const [selectedAgentData, setSelectedAgentData] = useState({});
  const [selectedModel, setSelectedModel] = useState('gpt-5.2'); // Default model
  const [incompleteMessage, setIncompleteMessage] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const [reasoningModeEnabled, setReasoningModeEnabled] = useState(false);
  const [showDocumentsDropdown, setShowDocumentsDropdown] = useState(false);
  const [documentsButtonRef, setDocumentsButtonRef] = useState(null);
  const [isDocumentEditMode, setIsDocumentEditMode] = useState(false);
  const [documentEditId, setDocumentEditId] = useState(null);
  const [regeneratingMessageIndex, setRegeneratingMessageIndex] = useState(null);
  
  // Initialize chat communication service - ALWAYS use HTTP
  const chatService = React.useMemo(() => {
    const communicationType = 'http'; // Force HTTP for all agents
    return new ChatCommunicationService(communicationType);
  }, []);

  // Helper function to check if all chunks are received and finalize message
  const checkAndFinalizeMessage = useCallback((messageId, conversationUuid) => {
    const expectedCount = expectedChunksRef.current.get(messageId);
    const chunksMap = streamingChunksRef.current.get(messageId);
    
    if (!expectedCount || !chunksMap) return false;
    
    // Check if we have all chunks (0 to expectedCount-1)
    if (chunksMap.size >= expectedCount) {
      const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
      const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
      
      // Update message as complete
      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const existingIndex = newMessages.findIndex(m => m.messageId === messageId);
        
        if (existingIndex >= 0) {
          newMessages[existingIndex] = { 
            ...newMessages[existingIndex], 
            message: reconstructedContent,
            isStreaming: false 
          };
        }
        
        return newMessages;
      });
      
      // Cleanup refs
      streamingMessagesRef.current.delete(messageId);
      streamingChunksRef.current.delete(messageId);
      expectedChunksRef.current.delete(messageId);
      setAgentIsTyping(false);
      setIsSendMessageClicked(false);
      
      return true;
    }
    
    return false;
  }, []);

  // WebSocket subscription for CONVERSATION scope with DELTA event
  // Handles streaming chunks - does NOT trigger cleanup (waits for all chunks)
  useWebSocketScope(
    WebSocketScope.CONVERSATION,
    useCallback((message) => {
      const payload = message.payload || {};
      const { chunkContent, chunkIndex, conversationUuid, messageId } = payload;
      
      // Strict filter by conversation - only accept messages for our conversation
      const currentConversationId = historyAgentStore.lastConversationID;
      
      if (currentConversationId) {
        if (!conversationUuid || conversationUuid !== currentConversationId) {
          return;
        }
      } else {
        return;
      }
      
      // Handle empty content
      if (!chunkContent) {
        return;
      }
      
      // Initialize tracking for new message
      if (!streamingChunksRef.current.has(messageId)) {
        streamingChunksRef.current.set(messageId, new Map());
        streamingMessagesRef.current.set(messageId, '');
        setAgentIsTyping(false);
      }
      
      // Store chunk by index
      const chunksMap = streamingChunksRef.current.get(messageId);
      const effectiveIndex = typeof chunkIndex === 'number' ? chunkIndex : chunksMap.size;
      chunksMap.set(effectiveIndex, chunkContent);
      
      // Reconstruct content in order (sorted by chunk index)
      const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
      const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
      streamingMessagesRef.current.set(messageId, reconstructedContent);
      
      // Update message in state (still streaming)
      setMessages(prevMessages => {
        const newMessages = [...prevMessages];
        const existingIndex = newMessages.findIndex(m => m.messageId === messageId);
        
        const messageData = {
          message: reconstructedContent,
          type: 'received',
          time: dayjs().format('HH:mm'),
          model: selectedModel,
          messageId: messageId,
          conversationUuid: conversationUuid,
          isStreaming: true,
        };
        
        if (existingIndex >= 0) {
          newMessages[existingIndex] = { ...newMessages[existingIndex], ...messageData };
        } else {
          newMessages.push(messageData);
        }
        
        return newMessages;
      });
      
      // Check if all chunks are now received
      checkAndFinalizeMessage(messageId, conversationUuid);
    }, [selectedModel, historyAgentStore.lastConversationID, checkAndFinalizeMessage]),
    'DELTA'
  );

  // WebSocket subscription for CONVERSATION scope with COMPLETE event
  // Stores the expected chunk count and the final chunk, then checks if all chunks are received
  useWebSocketScope(
    WebSocketScope.CONVERSATION,
    useCallback((message) => {
      const payload = message.payload || {};
      const { chunkContent, chunkIndex, conversationUuid, messageId } = payload;
      
      // Strict filter by conversation
      const currentConversationId = historyAgentStore.lastConversationID;
      if (currentConversationId) {
        if (!conversationUuid || conversationUuid !== currentConversationId) {
          return;
        }
      } else {
        return;
      }
      
      if (!messageId) return;
      
      // Store the expected total chunk count (chunkIndex + 1 since indices are 0-based)
      if (typeof chunkIndex === 'number') {
        expectedChunksRef.current.set(messageId, chunkIndex + 1);
      }
      
      // Initialize tracking for new message if needed
      if (!streamingChunksRef.current.has(messageId)) {
        streamingChunksRef.current.set(messageId, new Map());
        streamingMessagesRef.current.set(messageId, '');
        setAgentIsTyping(false);
      }
      
      // Store chunk content if present
      if (chunkContent) {
        const chunksMap = streamingChunksRef.current.get(messageId);
        const effectiveIndex = typeof chunkIndex === 'number' ? chunkIndex : chunksMap.size;
        chunksMap.set(effectiveIndex, chunkContent);
        
        const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
        const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
        streamingMessagesRef.current.set(messageId, reconstructedContent);
        
        // Update message in state (still may be streaming if other chunks pending)
        setMessages(prevMessages => {
          const newMessages = [...prevMessages];
          const existingIndex = newMessages.findIndex(m => m.messageId === messageId);
          
          const messageData = {
            message: reconstructedContent,
            type: 'received',
            time: dayjs().format('HH:mm'),
            model: selectedModel,
            messageId: messageId,
            conversationUuid: conversationUuid,
            isStreaming: true, // Will be set to false by checkAndFinalizeMessage
          };
          
          if (existingIndex >= 0) {
            newMessages[existingIndex] = { ...newMessages[existingIndex], ...messageData };
          } else {
            newMessages.push(messageData);
          }
          
          return newMessages;
        });
      }
      
      // Check if all chunks are now received
      checkAndFinalizeMessage(messageId, conversationUuid);
    }, [selectedModel, historyAgentStore.lastConversationID, checkAndFinalizeMessage]),
    'COMPLETE'
  );

  const handleSelectQuestion = (selectedQuestion) => {
    setNewMessage(selectedQuestion);
    handleSendMessage(selectedQuestion);
  };

  // Add a handler to add reasoning agent messages to the chat
  const handleAddReasoningMessage = useCallback((messageData) => {
    setMessages(prevMessages => [...prevMessages, {
      ...messageData,
      agentType: 'reasoning',
    }]);
  }, []);

  const handleAgentSelect = (name, agent_uuid) => {
    if (agent_uuid === agent_params.agent) {
      return;
    }
    
    setMessages([]);
    historyAgentStore.clearMessagesData();
    historyAgentStore.setLastConversationID("");
    historyAgentStore.setIsChatQuestionsVisible(true);
    setPersistentFileUuids([]); // Clear persistent file UUIDs when switching agents
    
    // Clear DAG progress when switching agents
    setChatDagProgress([]);
    setChatCurrentDagNode(null);
    setIsChatReasoningActive(false);
    
    const selectedAgent = authStore.agents.find(
      (agent) => agent.uuid === agent_uuid,
    );
    
    setSelectedAgentData(selectedAgent);
    setSelectedAgentName(name);

    setAgent_params((prevParams) => ({
      ...prevParams,
      agent: selectedAgent?.uuid,
    }));
    
    // Load agent into playgroundConfigStore for vault access (same as playground does)
    if (agent_uuid) {
      playgroundConfigStore.agentStore.fetchAgent(agent_uuid).then(() => {
        // After loading full agent data, update conversation starters from playground store
        const fullAgentStarters = playgroundConfigStore.agentStore.starters;
        
        if (fullAgentStarters && fullAgentStarters.length > 0) {
          // Update the selected agent with the full starters
          const updatedAgent = {
            ...selectedAgent,
            conversation_starters: fullAgentStarters
          };
          historyAgentStore.setConversationsStarters(updatedAgent);
        } else {
          // Use original agent (will fall back to defaults)
          historyAgentStore.setConversationsStarters(selectedAgent);
        }
      }).catch(error => {
        console.error('Error loading agent vault data:', error);
        // Fallback to original agent
        historyAgentStore.setConversationsStarters(selectedAgent);
      });
    } else {
      historyAgentStore.setConversationsStarters(selectedAgent);
    }
  };

  const bottomRef = useRef(null);
  const textAreaRef = useRef();

  useEffect(() => {
    if (authStore.selectedAccount && authStore.selectedAccount.id) {
      setSelectedCustomer(authStore.selectedAccount.id);
      conversationsStore.setCustomerId(authStore.selectedAccount.id);
      authStore.fetchAgents();
    }
  }, [authStore.selectedAccount]);

  useEffect(() => {
    // authStore.fetchAgents() already fetches all agents via HTTP
    // No need to call GraphQL getAgents or fetchConciergeAgent
    historyAgentStore.setBottomRef(bottomRef);
  }, [historyAgentStore]);

  useEffect(() => {
    const agentIdFromUrl = searchParams.get("agent");
    
    const selectedAgent = authStore.agents.find(
      (agent) => agent.uuid === agentIdFromUrl || agent.id === agentIdFromUrl,
    );
    const conciergeAgent = authStore.agents.find(
      (agent) => agent.domain === "concierge",
    );
    
    if (selectedAgent) {
      handleAgentSelect(selectedAgent.name, selectedAgent.uuid);
    } else if (conciergeAgent) {
      handleAgentSelect(conciergeAgent.name, conciergeAgent.uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authStore.agents]);

  useEffect(() => {
    if (messages.length && historyAgentStore.bottomRef?.current) {
      scrollToBottom();
    }
  }, [messages]);

  // Clear local messages immediately when loading starts to prevent showing old messages
  useEffect(() => {
    if (historyAgentStore.isLoadingMessages) {
      setMessages([]);
    }
  }, [historyAgentStore.isLoadingMessages]);

  // Update messages when messagesData changes (from conversation switch or new messages)
  useEffect(() => {
    // Only update if we're not loading (prevents flash of empty content)
    if (!historyAgentStore.isLoadingMessages) {
      if (historyAgentStore.messagesData.length) {
        setAgentIsTyping(false);
        historyAgentStore.setIsChatQuestionsVisible(false);
        setMessages(toJS(historyAgentStore.messagesData));
      } else if (!historyAgentStore.lastConversationID) {
        // Only clear messages if there's no active conversation
        setMessages([]);
        historyAgentStore.setIsChatQuestionsVisible(true);
      }
    }
  }, [historyAgentStore.messagesData, historyAgentStore.isLoadingMessages]);

  // Handle shouldRefreshMessages flag to update UI state
  useEffect(() => {
    if (historyAgentStore.shouldRefreshMessages && historyAgentStore.messagesData.length > 0) {
      historyAgentStore.setIsChatQuestionsVisible(false);
      historyAgentStore.setShouldRefreshMessages(false);
    }
  }, [historyAgentStore.shouldRefreshMessages, historyAgentStore.messagesData]);

  useEffect(() => {
    if (!historyAgentStore.isLoadingAgents && !historyAgentStore.isLoadingMessages) {
      setIsInitialLoading(false);
    }
  }, [historyAgentStore.isLoadingAgents, historyAgentStore.isLoadingMessages]);

  const handleTextAreaChange = (e) => {
    setNewMessage(e.target.value);
    const textArea = textAreaRef.current;
    
    if (textArea && textArea.style) {
      textArea.style.height = "auto";
      const scrollHeight = textArea.scrollHeight;
      const newHeight = (Math.max(48, scrollHeight));
      setChatWindowHeight(newHeight);
      textArea.style.height = `${newHeight}px`;
    }
  };

  const handleSendMessage = async (message, options = {}) => {
    const messageToSend = message || newMessage;
    const { useWebSearch = webSearchEnabled, silent = false, isFileUpload = false, fileData = null } = options;
    historyAgentStore.setIsChatQuestionsVisible(false);

    // Check if there's a file attachment (declared once at function start)
    const hasAttachment = !!(attachment?.url || attachment);
    
    // Allow empty messages only for file uploads
    if (messageToSend.trim() === "" && !isFileUpload) return;

    // If reasoning mode is enabled, send to reasoning agent instead
    if (reasoningModeEnabled) {
      handleSendToReasoningAgent(messageToSend, messageToSend);
      setReasoningModeEnabled(false); // Turn off reasoning mode after sending
      setNewMessage(""); // Clear input
      return;
    }

    // Check if this is a special chat tool message (like "contacts")
    const chatTool = detectChatTool(messageToSend);
    
    if (chatTool) {
      // Add user message
      const userMessage = {
        message: messageToSend,
        type: "sent",
        time: dayjs().format("HH:mm")
      };
      
      const local_msgs = [...messages, userMessage];
      setMessages(local_msgs);
      setNewMessage("");
      setIsSendMessageClicked(true);
      
      // Simulate agent "thinking" response
      setAgentIsTyping(true);
      const isReasoningTool = chatTool.type === 'reasoning';
      if (!isReasoningTool) {
        const thinkingMessage = {
          message: "Checking knowledge base...",
          type: "received", 
          time: dayjs().format("HH:mm"),
          isThinking: true
        };
        
        setMessages([...local_msgs, thinkingMessage]);
      }
      
      // After a brief delay, show the actual tool response
      setTimeout(() => {
        setAgentIsTyping(false);
        
        let toolResponse;
        if (chatTool.type === 'contacts') {
          toolResponse = {
            message: "contacts", // This triggers the ContactsChatTool
            type: "received",
            time: dayjs().format("HH:mm"),
            chatTool: chatTool
          };
        } else if (chatTool.type === 'vehicles') {
          toolResponse = {
            message: "vehicles", // This triggers the VehiclesChatTool
            type: "received", 
            time: dayjs().format("HH:mm"),
            chatTool: chatTool
          };
        }
        
        // Replace the thinking message with the tool
        const finalMessages = [...local_msgs, toolResponse];
        setMessages(finalMessages);
        setIsSendMessageClicked(false);
      }, 1500); // 1.5 second delay to simulate processing
      
      return; // Don't send to agent
    }

    setIsSendMessageClicked(true);

    let local_msgs = [...messages];
    
    // If this is a file upload, create a file message (local display only)
    if (isFileUpload && fileData) {
      const fileMessage = {
        type: "file",
        content: "Attached file:",
        files: [{
          name: fileData.name,
          type: fileData.type,
          size: fileData.size
        }],
        time: dayjs().format("HH:mm")
      };
      
      local_msgs = [...local_msgs, fileMessage];
      setMessages(local_msgs);
      
      // Clear attachment after displaying file message
      setAttachment({ url: "", fileName: "", fileSize: "" });
      setIsSendMessageClicked(false);
      
      // Don't send to agent - just display locally
      return;
    }
    
    // Add user message (ONLY if not silent)
    if (!silent) {
      local_msgs = [...local_msgs, {
        message: messageToSend,
        type: "sent",
        time: dayjs().format("HH:mm")
      }];

      setMessages(local_msgs);
    }

    setNewMessage("");

    // Force HTTP API for all messages (not GraphQL)
    // ALWAYS use HTTP endpoint - GraphQL is deprecated
    try {
      setAgentIsTyping(true);
      setShowSpinner(false);
      
      // HTTP-based communication for all agents
      const assistant_uuid = selectedAgentData.uuid || selectedAgentData.id;
      
      if (!assistant_uuid) {
        throw new Error('No assistant UUID available');
      }
      
      // Only use persistent file UUIDs (reset after first use)
      // Don't extract from chat messages - they're just for UI display
      const allFileUuids = [...persistentFileUuids];
      const isNewConversation = !historyAgentStore.lastConversationID;

      const params = {
          message_text: messageToSend,
          tenant_id: authStore.selectedAccount?.tenantId || "6bf81e8b-3041-4f0d-9cbd-52ab9daaf2b9",
          sender_uuid: authStore.selectedAccount?.id || selectedCustomer,
        assistant_uuid: assistant_uuid,
          conversation_uuid: historyAgentStore.lastConversationID || "",
          channel: "",
          metadata: { useWebSearch },
          attachment: (attachment?.url || attachment) || undefined,
          // Include user_uuid for x-user-uuid header
          user_uuid: authStore.userId,
          // Include file_uuids if there are any file attachments with documentUuid
          ...(allFileUuids.length > 0 && {
            file_uuids: allFileUuids
          }),
          // Include LLM config if a specific model is selected
          ...(selectedModel && {
            llmConfig: {
              llm_model: selectedModel,
              llm_model_provider: "openai"
            }
          }),
          // Concierge agent specific params
          ...(assistant_uuid === process.env.REACT_APP_CONCIERGE_AGENT_UUID && {
            params: {
              concierge: {
                agents: [
                  {
                    uuid: "db3c67a5-0deb-447b-a8e2-ab7fc6568a7b",
                    name: "Orion",
                    description: "This agent is here to guide users through clear, accurate, and professional vehicle comparisons using authoritative OEM vehicle data from model years 2007 through 2026, while maintaining empathy, clarity, and consultative expertise."
                  },
                  {
                    uuid: "089db85a-a16b-403f-897b-cd3210a781f7",
                    name: "Atlas",
                    description: "Atlas is an Inventory Specialist Coach for dealership sales reps. It produce a fast, confident training \"Crash Course\" on a specific pre-owned vehicle in inventory so a rep can sell it even if they've never been trained on it."
                  },
                  {
                    uuid: "62a3a364-6ed7-4b17-966d-9c7cfc035ae5",
                    name: "Objection Handling Coach",
                    description: "A dealership objection-handling trainer for BDC + floor in a negotiation store. Enter a customer objection to get 20-second talk tracks for Phone, Text, Email, and In-Person using the playbook: Validate → Clarify → Isolate → 2 Paths → Next Step. You can also paste what you said and get a cleaner rewrite + coaching, or request a 5-objection drill for training."
                  }
                ]
              }
            }
          })
        };

      const result = await chatService.sendMessage(params);
        
        if (result.success) {
          // Clear attachment after successful message send
          setAttachment({ url: "", fileName: "", fileSize: "" });
          // DON'T reset agentIsTyping here - wait for first chunk from WebSocket
          // setAgentIsTyping(false); // Removed - will be reset when first chunk arrives
          
          // Update conversation ID from response
          // Response structure: { body: { conversation_uuid: "...", message_uuid: "...", dispatch_status: "..." } }
          const conversationId = result.data?.body?.conversation_uuid || 
                                 result.data?.conversationUuid || 
                                 result.data?.conversation_id;
          
          if (conversationId) {
            historyAgentStore.setLastConversationID(conversationId);

            // Title only when new conversation (first message). Existing conversations: don't pass title so it stays unchanged.
            const conversationPayload = {
              id: conversationId,
              last_modified: new Date().toISOString(),
              agent_id: selectedAgentData.uuid,
            };
            if (isNewConversation) {
              conversationPayload.title = messageToSend;
            }
            historyAgentStore.addConversation(conversationPayload);
            historyAgentStore.setSelectedConversationId(conversationId);
          }
          
          // Reset file_uuids after sending (only use once per upload)
          if (allFileUuids.length > 0) {
            setPersistentFileUuids([]);
          }
          
          // DON'T add assistant response here - wait for chunks from WebSocket
          // The message will be created when the first chunk arrives via WebSocket
        
        // DON'T reset isSendMessageClicked here - will be reset when last chunk arrives
        // setIsSendMessageClicked(false); // Removed - will be reset when last chunk arrives
      } else {
          throw new Error(result.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setShowSpinner(false);
      setAgentIsTyping(false);
      setIsSendMessageClicked(false);
      
      const errorMessage = error.errors?.[0]?.errorType === "Lambda:ExecutionTimeoutException"
        ? "Sorry, I'm taking longer than expected to get your answer. I'll notify my team to teach me more about this topic. Meanwhile, let's continue with other questions you have?"
        : "Oops! Something went wrong. Please try again.";
      
      setMessages(prevMessages => [
        ...prevMessages,
        {
          message: errorMessage,
          type: "received",
          time: dayjs().format("HH:mm"),
        },
      ]);
    }
  };

  // Handler for sending selected chat text to reasoning agent
  // Must be declared after handleSendMessage to avoid initialization errors
  const handleSendToReasoningAgent = async (instruction, selectedText) => {
    // The instruction from TextSelectionMenu already includes the selectedText formatted with the action
    // (e.g., "Improve this text while maintaining its meaning:\n\n${selectedText}")
    // If instruction is provided, use it. Otherwise, format selectedText directly.
    const messageToSend = instruction || (selectedText ? `Process this text:\n\n${selectedText}` : "Processing selected text...");
    
    // Add user message to chat
    const userMessage = {
      message: messageToSend,
      type: "sent",
      time: dayjs().format("HH:mm")
    };
    setMessages(prev => [...prev, userMessage]);

    // Reset DAG state
    setChatDagProgress([]);
    setChatCurrentDagNode(null);
    setIsChatReasoningActive(true);
    chatDagProgressRef.current = []; // Reset ref
    
    try {
      // Call reasoning agent directly
      const chatService = new ChatCommunicationService('http');
      const params = {
        message_text: messageToSend, // Always includes the selected text (either in instruction or formatted)
        conversation_uuid: null, // Let reasoning agent create new thread
        sender_uuid: authStore.selectedAccount?.id || selectedCustomer,
        onProgress: (progressUpdate) => {
          if (!progressUpdate?.nodeName) return;
          setChatDagProgress(prev => {
            const index = prev.findIndex(item => item.nodeName === progressUpdate.nodeName);
            let updated;
            if (index >= 0) {
              updated = [...prev];
              updated[index] = { ...updated[index], ...progressUpdate };
            } else {
              updated = [...prev, progressUpdate];
            }
            // Keep ref in sync with state
            chatDagProgressRef.current = updated;
            return updated;
          });
          if (progressUpdate.status === 'running') {
            setChatCurrentDagNode(progressUpdate);
          } else if (progressUpdate.status === 'completed') {
            setChatCurrentDagNode(null);
          }
        },
      };
      
      setAgentIsTyping(true);
      const response = await chatService.sendReasoningAgentMessage(params);
      setAgentIsTyping(false);
      
      // Capture DAG progress from ref (always has latest value)
      const completedDagProgress = [...chatDagProgressRef.current];
      
      setIsChatReasoningActive(false);
      setChatCurrentDagNode(null);
      
      if (response.success) {
        // ALWAYS prioritize output.reasoning.result (reasoningResult field)
        // This is the definitive final answer from the reasoning agent
        const reasoningResult = response.data.reasoningResult;
        const messageText = response.data.messageText;
        
        // Use reasoningResult if available, otherwise fall back to messageText
        const finalResult = reasoningResult || messageText;
        
        if (finalResult && finalResult.trim().length > 0) {
          // IMPORTANT: Save DAG progress as a message BEFORE adding the result
          // This persists the execution plan in chat history
          if (completedDagProgress.length > 0) {
            const dagProgressMessage = {
              message: '', // Empty message, will render DAG component
              type: 'received',
              time: dayjs().format("HH:mm"),
              agentType: 'reasoning',
              dagProgress: completedDagProgress, // Store the DAG progress
              isDagProgressMessage: true, // Flag to identify DAG progress messages
              metadata: {
                isReasoningAgent: true,
                isDagProgress: true,
              }
            };
            handleAddReasoningMessage(dagProgressMessage);
          }
          
          // Add reasoning agent response to chat
          handleAddReasoningMessage({
            message: finalResult,
            type: 'received',
            time: dayjs().format("HH:mm"),
            agentType: 'reasoning',
            model: 'reasoning', // Reasoning agent uses its own model
            metadata: {
              isReasoningAgent: true,
              instruction: instruction,
            }
          });
          
          // Clear DAG progress state after saving to messages
          setChatDagProgress([]);
          chatDagProgressRef.current = [];
        } else {
          throw new Error('Reasoning agent returned empty result');
        }
      } else {
        throw new Error(response.error || 'Reasoning agent failed');
      }
    } catch (error) {
      console.error('Reasoning agent error:', error);
      setAgentIsTyping(false);
      setIsChatReasoningActive(false);
      setChatCurrentDagNode(null);
      setChatDagProgress([]);
      chatDagProgressRef.current = [];
      setMessages(prev => [...prev, {
        message: "Sorry, I couldn't process that request. Please try again.",
        type: "received",
        time: dayjs().format("HH:mm"),
      }]);
    }
  };

  const handleCleanThread = () => {
    setMessages([]);
    historyAgentStore.setIsChatQuestionsVisible(true);
    setAgentIsTyping(false);
    historyAgentStore.setLastConversationID("");
    setIsSendMessageClicked(false);
    historyAgentStore.setSelectedConversationId(null);
    setAttachment({ url: "", fileName: "", fileSize: "" });
    setPersistentFileUuids([]); // Clear persistent file UUIDs when cleaning thread
    // Clear DAG progress when starting new chat
    setChatDagProgress([]);
    setChatCurrentDagNode(null);
    setIsChatReasoningActive(false);
    chatDagProgressRef.current = [];
  };

  // Handler to send user messages from interactive components (like multi-selection)
  const handleSendUserMessage = (messageText, metadata = {}) => {
    const textToSend = typeof messageText === 'string' ? messageText : messageText?.message;
    if (textToSend) {
      // Detect if this is a wizard answer message (contains numbered questions)
      const isWizardMessage = metadata.isWizardAnswer || 
        /^\d+\.\s+.+\n\nSelected:/m.test(textToSend);
      
      handleSendMessage(textToSend, { 
        silent: isWizardMessage // Don't show wizard answers in chat
      });
    }
  };

  // Handler to toggle reasoning agent mode
  const handleActivateReasoningAgent = () => {
    setReasoningModeEnabled(prev => !prev);
  };


  // Handler to mark a wizard as submitted
  const handleMarkWizardSubmitted = (messageIndex, answersData) => {
    setMessages(prevMessages => {
      const updatedMessages = prevMessages.map((msg, idx) => 
        idx === messageIndex 
          ? { 
              ...msg, 
              wizardSubmitted: true,
              wizardAnswers: answersData?.answers || {},
              wizardCustomAnswers: answersData?.customAnswers || {}
            }
          : msg
      );
      return updatedMessages;
    });
  };

  // Handler to send reasoning agent response to regular agent
  const handleSendReasoningToRegularAgent = (reasoningMessage) => {
    // Send the reasoning agent's response to the regular agent
    handleSendMessage(reasoningMessage);
  };

  // Handle file upload to drives/folders and/or agents
  const handleUploadToVault = async (file, selectedDestinations, selectedAgents) => {
    try {
      if ((!selectedDestinations || selectedDestinations.length === 0) && 
          (!selectedAgents || selectedAgents.length === 0)) {
        throw new Error('No destinations or agents selected');
      }

      let firstS3Url = null;
      const results = {
        driveUploads: { success: [], failed: [] },
        agentAssignments: { success: [], failed: [] }
      };

      // PART 1: Upload to selected drive/folder destinations (if any)
      if (selectedDestinations && selectedDestinations.length > 0) {
        for (const destination of selectedDestinations) {
          try {
            const { vaultUuid, folderUuid, vaultName, folderName } = destination;

            // Create document entry
            const createDocumentBody = {
              name: file.name,
              vaultUuid,
              folderUuid: folderUuid || null,
              type: file.type || 'application/octet-stream',
              url: '',
              metadata: {
                source: "chat_attachment",
                version: "1.0",
                region: "us-east-1",
                confidential: true
              }
            };

            const createDocumentResponse = await createDocumentByTenant(createDocumentBody);
            const { status_code, body } = createDocumentResponse.data;
            
            if (status_code < 200 || status_code >= 300) {
              throw new Error('Failed to create document entry');
            }
            
            const documentUuid = body.document.document_uuid;
            const versionUuid = body.version.doc_version_uuid;

            // Create file entry and get upload URL
            const createFileBody = {
              name: file.name,
              folder_id: folderUuid || '-',
              tenant_id: authStore.selectedAccount?.tenantId || authStore.selectedAccount?.id,
              user_id: authStore.userId,
              file_type: file.type || 'application/octet-stream',
              status: 'active',
              vault_uuid: vaultUuid,
              document_uuid: documentUuid,
              version_uuid: versionUuid
            };

            const createFileResponse = await fileStore.createFile(createFileBody);

            if (!createFileResponse?.body?.uploadUrl) {
              throw new Error('No upload URL received');
            }

            const uploadUrl = createFileResponse.body.uploadUrl;
            const s3Key = createFileResponse.body.fileKey;

            // Upload file to S3
            const uploadResponse = await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
              }
            });

            if (!uploadResponse.ok) {
              throw new Error(`Upload failed: ${uploadResponse.status}`);
            }

            // Attach document
            const attachDocumentBody = {
              s3_key: s3Key,
              document_uuid: documentUuid,
            };

            const attachDocumentResponse = await attachDocumentbyTenant(attachDocumentBody);
            // Capture document_uuid from attach response (may be different from create)
            const attachedDocumentUuid = attachDocumentResponse?.data?.body?.uuid || documentUuid;
            
            // Store documentUuid immediately after upload
            setPersistentFileUuids(prev => {
              const updated = [...new Set([...prev, attachedDocumentUuid])];
              return updated;
            });

            // Store first S3 URL for chat message
            if (!firstS3Url) {
              firstS3Url = `s3://${s3Key}`;
            }

            results.driveUploads.success.push({ location: `${vaultName}/${folderName || 'Root'}`, s3Key, documentUuid: attachedDocumentUuid });
            
          } catch (error) {
            console.error(`❌ Error uploading to drive:`, error);
            results.driveUploads.failed.push({ location: destination.vaultName, error: error.message });
          }
        }
      }

      // PART 2: Assign to selected agents (if any)
      if (selectedAgents && selectedAgents.length > 0) {
        // If documents were already uploaded to drives in PART 1, just assign those to agents
        if (results.driveUploads.success.length > 0) {
          
          // Assign each uploaded document to all selected agents
          for (const upload of results.driveUploads.success) {
            const documentUuid = upload.documentUuid;
            
            for (const agent of selectedAgents) {
              try {
                // Update the file message in chat with documentUuid
                setMessages(prevMessages => {
                  return prevMessages.map(msg => {
                    if (msg.type === 'file' && msg.files?.[0]?.name === file.name) {
                      return {
                        ...msg,
                        files: [{
                          ...msg.files[0],
                          documentUuid
                        }]
                      };
                    }
                    return msg;
                  });
                });
                
                // Update persistentFileUuids to include this documentUuid
                setPersistentFileUuids(prev => {
                  const updated = [...new Set([...prev, documentUuid])];
                  return updated;
                });
                
                results.agentAssignments.success.push({ agent: agent.name, documentUuid });
              } catch (error) {
                console.error(`❌ Error assigning to agent ${agent.name}:`, error);
                results.agentAssignments.failed.push({ agent: agent.name, error: error.message });
              }
            }
          }
        } else {
          // No drives selected, upload to first agent's vault using global endpoint
          
          const firstAgent = selectedAgents[0];
          
          // Fetch first agent details to get vault UUID
          await playgroundConfigStore.agentStore.fetchAgent(firstAgent.uuid);
          const firstAgentVaultUuid = playgroundConfigStore.agentStore.vaultUuid;
          
          if (!firstAgentVaultUuid) {
            throw new Error(`No vault found for agent ${firstAgent.name}`);
          }
          
          // Create file entry and get upload URL (this is now the first step)
          const createFileBody = {
            name: file.name,
            folder_id: '-',
            tenant_id: authStore.selectedAccount?.tenantId || authStore.selectedAccount?.id,
            user_id: authStore.userId,
            file_type: file.type || 'application/octet-stream',
            status: 'active',
            vault_uuid: firstAgentVaultUuid
          };

          const createFileResponse = await fileStore.createFile(createFileBody);

          if (!createFileResponse?.body?.uploadUrl) {
            throw new Error('No upload URL received');
          }

          // Extract documentUuid from file response (body.file.uuid)
          const documentUuid = createFileResponse.body.file.uuid;
          
          // Store documentUuid immediately after file creation
          setPersistentFileUuids(prev => {
            const updated = [...new Set([...prev, documentUuid])];
            return updated;
          });

          const uploadUrl = createFileResponse.body.uploadUrl;
          const s3Key = createFileResponse.body.fileKey;

          // Upload file to S3
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            }
          });

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.status}`);
          }

          // Store S3 URL for chat message
          if (!firstS3Url) {
            firstS3Url = `s3://${s3Key}`;
          }
          
          // Assign this document to all selected agents (replaces attach step)
          for (const agent of selectedAgents) {
            try {            
              // Update the file message in chat with documentUuid
              setMessages(prevMessages => {
                return prevMessages.map(msg => {
                  if (msg.type === 'file' && msg.files?.[0]?.name === file.name) {
                    return {
                      ...msg,
                      files: [{
                        ...msg.files[0],
                        documentUuid
                      }]
                    };
                  }
                  return msg;
                });
              });
              
              // Update persistentFileUuids to include this documentUuid
              setPersistentFileUuids(prev => {
                const updated = [...new Set([...prev, documentUuid])];
                return updated;
              });
              
              results.agentAssignments.success.push({ agent: agent.name, s3Key, documentUuid });
            } catch (error) {
              console.error(`❌ Error assigning to agent ${agent.name}:`, error);
              results.agentAssignments.failed.push({ agent: agent.name, error: error.message });
            }
          }
        }
      }

      if (!firstS3Url) {
        throw new Error('All uploads failed');
      }

      return firstS3Url;
    } catch (error) {
      console.error('❌ Error in handleUploadToVault:', error);
      throw error;
    }
  };

  // Handle documents dropdown toggle
  const handleDocumentsClick = useCallback((event) => {
    setDocumentsButtonRef(event.currentTarget);
    setShowDocumentsDropdown(true);
  }, []);

  const handleDocumentsClose = useCallback(() => {
    setShowDocumentsDropdown(false);
    setDocumentsButtonRef(null);
  }, []);

  // Handle opening document from dropdown
  const handleOpenDocument = useCallback((document) => {
    // Set the document as current in store
    chatDocumentsStore.setCurrentDocument(document.id);
    // Enter document edit mode (80/20 split layout)
    setDocumentEditId(document.id);
    setIsDocumentEditMode(true);
  }, [chatDocumentsStore]);

  // Handle entering document edit mode (from "Edit as Document" button)
  const handleEnterDocumentEditMode = useCallback((documentId) => {
    chatDocumentsStore.setCurrentDocument(documentId);
    setDocumentEditId(documentId);
    setIsDocumentEditMode(true);
  }, [chatDocumentsStore]);

  // Handle exiting document edit mode
  const handleExitDocumentEditMode = useCallback(() => {
    chatDocumentsStore.clearCurrentDocument();
    setDocumentEditId(null);
    setIsDocumentEditMode(false);
  }, [chatDocumentsStore]);

  const scrollToBottom = useCallback(() => {
    if (bottomRef?.current) {
      bottomRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  }, [bottomRef]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (historyAgentStore.messagesData.length > 0) {
      scrollToBottom();
    }
  }, [historyAgentStore.messagesData, scrollToBottom]);

  const handleToggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };



  const handleRegenerateMessage = async (messageIndex) => {
    // Set regenerating state
    setRegeneratingMessageIndex(messageIndex);
    
    // Find the user message that triggered this response
    const userMessage = messages[messageIndex - 1];
    
    if (!userMessage || userMessage.type !== 'sent') {
      setRegeneratingMessageIndex(null);
      return;
    }
    
    try {
      // Remove the old agent response from messages
      const messagesWithoutOldResponse = messages.filter((_, index) => index !== messageIndex);
      setMessages(messagesWithoutOldResponse);
      
      // Resend the API request directly (without adding user message again)
      setAgentIsTyping(true);
      setShowSpinner(false);
      
      const assistant_uuid = selectedAgentData.uuid || selectedAgentData.id;
      
      // Only use persistent file UUIDs (reset after first use)
      // Don't extract from chat messages - they're just for UI display
      const allFileUuids = [...persistentFileUuids];
      
      const params = {
        message_text: userMessage.message,
        tenant_id: authStore.selectedAccount?.tenantId || "6bf81e8b-3041-4f0d-9cbd-52ab9daaf2b9",
        sender_uuid: authStore.selectedAccount?.id || selectedCustomer,
        assistant_uuid: assistant_uuid,
        conversation_uuid: historyAgentStore.lastConversationID || "",
        channel: "",
        metadata: { useWebSearch: webSearchEnabled },
        attachment: undefined,
        // Include user_uuid for x-user-uuid header
        user_uuid: authStore.userId,
        // Include file_uuids if there are any file attachments with documentUuid
        ...(allFileUuids.length > 0 && {
          file_uuids: allFileUuids
        }),
        // Include LLM config if a specific model is selected
        ...(selectedModel && {
          llmConfig: {
            llm_model: selectedModel,
            llm_model_provider: "openai"
          }
        })
      };

      const result = await chatService.sendMessage(params);
      
      if (result.success) {
        // DON'T reset agentIsTyping here - wait for first chunk from WebSocket
        // setAgentIsTyping(false); // Removed - will be reset when first chunk arrives
        
        // Update conversation ID from response
        // Response structure: { body: { conversation_uuid: "...", message_uuid: "...", dispatch_status: "..." } }
        const conversationId = result.data?.body?.conversation_uuid || 
                               result.data?.conversationUuid || 
                               result.data?.conversation_id;
        
        if (conversationId) {
          historyAgentStore.setLastConversationID(conversationId);
        }
        
        // Reset file_uuids after sending (only use once per upload)
        if (allFileUuids.length > 0) {
          setPersistentFileUuids([]);
        }
        
        // DON'T add assistant response here - wait for chunks from WebSocket
        // The message will be created when the first chunk arrives via WebSocket
      } else {
        throw new Error(result.error || "Failed to regenerate message");
      }
    } catch (error) {
      console.error('❌ Error regenerating message:', error);
      setAgentIsTyping(false);
      
      // Restore the old message on error
      setMessages(messages);
    } finally {
      // Clear regenerating state
      setRegeneratingMessageIndex(null);
    }
  };

  return (
    <>
      {/* <MainContainer>
        <BreadCrumbContainer>
          <Title>Home</Title>
        </BreadCrumbContainer>
      </MainContainer> */}
      <HomeContainer>
        <SidebarAgents
          isExpanded={isSidebarExpanded}
          onClick={handleToggleSidebar}
          selectedDefault={selectedAgentName}
          handleAgentSelect={handleAgentSelect}
          isCollapsedForDocument={isDocumentEditMode}
        />
        <MainArea isDesktop={isDesktop} isMobile={isMobile} $isDocumentEditMode={isDocumentEditMode}>
          {isInitialLoading ? (
            <CenteredSpinner>
              <Spinner className="spinner" data-testid="spinner" />
            </CenteredSpinner>
          ) : isDocumentEditMode ? (
            <DocumentEditLayout
              documentId={documentEditId}
              onCloseDocument={handleExitDocumentEditMode}
              chatMessages={messages}
              bottomRef={bottomRef}
              selectedAgent={selectedAgentData}
              agentIsTyping={agentIsTyping}
              onAddChatMessage={handleSendUserMessage}
              onSendToReasoningAgent={handleSendToReasoningAgent}
              onSendReasoningToRegularAgent={handleSendReasoningToRegularAgent}
              onMarkWizardSubmitted={handleMarkWizardSubmitted}
              reasoningProgress={
                (isChatReasoningActive || chatDagProgress.length > 0)
                  ? {
                      dagProgress: chatDagProgress,
                      currentDagNode: chatCurrentDagNode,
                      isActive: isChatReasoningActive,
                    }
                  : null
              }
              customerId={authStore.selectedAccount.id}
              textAreaRef={textAreaRef}
              handleTextAreaChange={handleTextAreaChange}
              showSpinner={showSpinner}
              newMessage={newMessage}
              handleSendMessage={handleSendMessage}
              attachment={attachment}
              setAttachment={setAttachment}
              isSendMessageClicked={isSendMessageClicked}
              handleCleanThread={handleCleanThread}
              selectedAgentData={selectedAgentData}
              webSearchEnabled={webSearchEnabled}
              setWebSearchEnabled={setWebSearchEnabled}
              reasoningModeEnabled={reasoningModeEnabled}
              handleActivateReasoningAgent={handleActivateReasoningAgent}
              chatDocumentsStore={chatDocumentsStore}
              handleDocumentsClick={handleDocumentsClick}
              showDocumentsDropdown={showDocumentsDropdown}
              documentsButtonRef={documentsButtonRef}
              handleDocumentsClose={handleDocumentsClose}
              handleOpenDocument={handleOpenDocument}
              vaultUuid={selectedAgentData?.vaultUuid}
              enableAICopilot={true}
            />
          ) : !selectedAgentData.uuid && !selectedAgentData.id ? (
            <EmptyAgentState />
          ) : (
            <ChatContainer className="tabs" isChatQuestionsVisible={historyAgentStore.isChatQuestionsVisible}>
              <BodyChatContainer isTablet={isTablet} isMobile={isMobile} isChatQuestionsVisible={historyAgentStore.isChatQuestionsVisible}>
                <ChatConversationContainer 
                  isChatQuestionsVisible={historyAgentStore.isChatQuestionsVisible}
                  isMobile={isMobile}
                  className="chat-conversation-container"
                >
                  <ChatWindowWrapper>
                    {selectedAgentData.name && historyAgentStore.isChatQuestionsVisible && (
                      <WelcomeContainer>
                        <ImageContainer>
                          {selectedAgentData.photo_url ? (
                            <AgentImage
                              src={selectedAgentData.photo_url}
                              alt={`${selectedAgentData.name} profile`}
                            />
                          ) : (
                            <Icon name="userAuthLogo" dataTestId="icon-chat-img" height="60px" width="60px" />
                          )}
                        </ImageContainer>
                        <AgentNameText>{selectedAgentData.name}</AgentNameText>
                        <AgentWelcomeContainer>
                          <AgentWelcomeText title={selectedAgentData.job_title && selectedAgentData.job_title}>
                            {truncateText(selectedAgentData.job_title || `Hello! I'm ${selectedAgentData.name}, feel free to type a message or select a suggestion to begin our chat.
I'll use information from our organization to provide you with responses.`)}
                          </AgentWelcomeText>
                        </AgentWelcomeContainer>
                      </WelcomeContainer>
                    )}

                    {/* Loading state when switching conversations */}
                    {historyAgentStore.isLoadingMessages && !historyAgentStore.isChatQuestionsVisible && (
                      <CenteredSpinner>
                        <Spinner className="spinner" data-testid="conversation-loading-spinner" />
                      </CenteredSpinner>
                    )}
                    
                    {!historyAgentStore.isChatQuestionsVisible && !historyAgentStore.isLoadingMessages && (
                      <HomePageChatWindow
                        selectedAgent={selectedAgentData}
                        messages={messages}
                        bottomRef={bottomRef}
                        height="100%"
                        chatWindowHeight={chatWindowHeight}
                        width="100%"
                        agentIsTyping={agentIsTyping}
                        dataTestId="chat-window-component"
                        onAddChatMessage={handleSendUserMessage}
                        onSendToReasoningAgent={handleSendToReasoningAgent}
                        onSendReasoningToRegularAgent={handleSendReasoningToRegularAgent}
                        onEnterDocumentEditMode={handleEnterDocumentEditMode}
                        onRegenerateMessage={handleRegenerateMessage}
                        regeneratingMessageIndex={regeneratingMessageIndex}
                        onMarkWizardSubmitted={handleMarkWizardSubmitted}
                        reasoningProgress={
                          (isChatReasoningActive || chatDagProgress.length > 0)
                            ? {
                                dagProgress: chatDagProgress,
                                currentDagNode: chatCurrentDagNode,
                                isActive: isChatReasoningActive,
                              }
                            : null
                        }
                      />
                    )}
                  </ChatWindowWrapper>

                  {historyAgentStore.isChatQuestionsVisible && (
                    <ChatQuestionsSection
                      onSelectQuestion={handleSelectQuestion}
                      isLoading={historyAgentStore.isLoadingConcierge}
                      loadedQuestions={historyAgentStore.agentQuestions}
                      selectedAgentName={selectedAgentData.name}
                      dataTestId="chat-questions-section"
                      onKeyPress={(event) => {
                        event.preventDefault();
                        if (event.key === 'Enter') {
                          handleSelectQuestion(event.target.value);
                        }
                      }}
                    />
                  )}
                </ChatConversationContainer>
              </BodyChatContainer>

                <ResponsiveFooterChatContainer className="footer-chat-container" attachment={!!attachment}>
                  {(isChatReasoningActive || chatDagProgress.length > 0) && (
                    <div>
                      <DAGProgressIndicator
                        dagProgress={chatDagProgress}
                        currentDagNode={chatCurrentDagNode}
                        isVisible={isChatReasoningActive || chatDagProgress.length > 0}
                        isComplete={!isChatReasoningActive && chatDagProgress.length > 0}
                      />
                    </div>
                  )}
                  
                  <ChatInputWrapper>
                  <>
                    <Separator>
                      {showSpinner && <Spinner className="spinner" />}
                    </Separator>
                    <ChatInput
                      customerId={authStore.selectedAccount.id}
                      textAreaRef={textAreaRef}
                      onChange={handleTextAreaChange}
                      onKeyPress={(e, callback) => {
                        if (e.key === "Enter" && !e.shiftKey && !showSpinner) {
                          e.preventDefault();
                          handleSendMessage();
                          callback("");
                        }
                      }}
                      showSpinner={showSpinner}
                      newMessage={newMessage}
                      handleSendMessage={handleSendMessage}
                      onFileUploaded={setAttachment}
                      disableSend={isSendMessageClicked}
                      onCleanThread={handleCleanThread}
                      showWebSearch={selectedAgentData?.actions?.includes('web_search')}
                      webSearchEnabled={webSearchEnabled}
                      onWebSearchToggle={setWebSearchEnabled}
                      showReasoningAgent={true}
                      reasoningModeEnabled={reasoningModeEnabled}
                      onReasoningAgentClick={handleActivateReasoningAgent}
                      showDocuments={true}
                      documentCount={chatDocumentsStore.documentCount}
                      onDocumentsClick={handleDocumentsClick}
                      showModelSelector={true}
                      selectedModel={selectedModel}
                      onModelChange={setSelectedModel}
                      onUploadToVault={handleUploadToVault}
                      currentAgent={selectedAgentData}
                      availableAgents={authStore.agents}
                    />
                    
                    {/* Documents Dropdown */}
                    <DocumentsDropdown
                      anchorEl={documentsButtonRef}
                      open={showDocumentsDropdown}
                      onClose={handleDocumentsClose}
                      onSelectDocument={handleOpenDocument}
                    />
                  </>
                </ChatInputWrapper>
              </ResponsiveFooterChatContainer>
            </ChatContainer>
          )}
        </MainArea>
      </HomeContainer>
      <CustomGenericListStyle />
    </>
  );
});

export default HomePage;
